import {
  dialCodes,
  isValidEmail,
  isValidPhone,
  isValidUrl,
  SwitchButton,
  tokens,
} from "@/utilities";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { EditableInput } from "@/utilities/Components";
import { useEffect, useMemo, useState } from "react";
import { CareerProps, ContactSocialMedia, DefaultValue, InstituteProps, facultyProps } from "@/types";
import careersService from "@/api/careers.service";
import Swal from "sweetalert2";
import {
  resolveCareerContactInfo,
  resolveCareerSocialMedia,
  resolveFacultyContactInfo,
  resolveFacultySocialMedia,
  resolveInstituteContactInfo,
  resolveInstituteSocialMedia,
} from "./formaters.hook";

interface Props {
  premiumPlan: string;
  isEditable: boolean;
  selectedCareer: CareerProps;
  updateCareer: (careerMap: Partial<CareerProps>, id: string) => void;
  fieldsToEdit: string;
  institute?: InstituteProps | null;
  faculty?: facultyProps | null;
}

const buildInitialContactState = (
  career: CareerProps,
  inst?: InstituteProps | null,
  fac?: facultyProps | null
) => {
  const defaultInfo = (career.contactInfo?.default || "ies") as DefaultValue;
  const defaultSocial = (career.contactSocialMedia?.default || "ies") as DefaultValue;

  const resolvedIes = resolveInstituteContactInfo(inst, career.contactInfo?.ies);
  const resolvedFaculty = resolveFacultyContactInfo(fac, resolvedIes, career.contactInfo?.faculty);
  const resolvedCareer = resolveCareerContactInfo(career, resolvedFaculty, resolvedIes);

  const resolvedIesSocial = resolveInstituteSocialMedia(inst, career.contactSocialMedia?.ies);
  const resolvedFacultySocial = resolveFacultySocialMedia(fac, resolvedIesSocial, career.contactSocialMedia?.faculty);
  const resolvedCareerSocial = resolveCareerSocialMedia(career);

  return {
    defaultInfo,
    defaultSocial,
    contactInfo: {
      default: defaultInfo,
      ies: resolvedIes,
      faculty: resolvedFaculty,
      career: resolvedCareer,
    },
    contactSocialMedia: {
      default: defaultSocial,
      ies: resolvedIesSocial,
      faculty: resolvedFacultySocial,
      career: resolvedCareerSocial,
    },
    webSite: resolvedCareer.webSite || resolvedIes.webSite || "",
    webSiteFaculty: resolvedFaculty.webSite || "",
    webSiteCareer: resolvedCareer.webSite || "",
  };
};

export const ContactInfo = ({
  premiumPlan,
  isEditable,
  selectedCareer,
  updateCareer,
  fieldsToEdit,
  institute,
  faculty,
}: Props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  //--------------Social Medias---------------
  const [whatsappError, setWhatsappError] = useState(false);
  const [facebookError, setFacebookError] = useState(false);
  const [linkedinError, setLinkedinError] = useState(false);

  const [isEditingContactInfo, setIsEditingContactInfo] =
    useState<boolean>(false);

  const initialState = useMemo(
    () => buildInitialContactState(selectedCareer, institute, faculty),
    [selectedCareer, institute, faculty]
  );

  const [defaultContactInfoValue, setDefaultContactInfoValue] =
    useState<DefaultValue>(initialState.defaultInfo);
  const [defaultContactSocialValue, setDefaultContactSocialValue] =
    useState<DefaultValue>(initialState.defaultSocial);

  const [editableContactInfo, setEditableContactInfo] = useState<
    Partial<CareerProps>
  >({
    webSite: initialState.webSite,
    webSiteFaculty: initialState.webSiteFaculty,
    webSiteCareer: initialState.webSiteCareer,
    contactInfo: initialState.contactInfo,
    contactSocialMedia: initialState.contactSocialMedia,
  });

  const [country, setCountry] = useState<string>(
    initialState.contactInfo[initialState.defaultInfo]?.phone_code_country || "+57"
  );

  useEffect(() => {
    if (!isEditingContactInfo) {
      const state = buildInitialContactState(selectedCareer, institute, faculty);
      setDefaultContactInfoValue(state.defaultInfo);
      setDefaultContactSocialValue(state.defaultSocial);
      setEditableContactInfo({
        webSite: state.webSite,
        webSiteFaculty: state.webSiteFaculty,
        webSiteCareer: state.webSiteCareer,
        contactInfo: state.contactInfo,
        contactSocialMedia: state.contactSocialMedia,
      });
      const pref = state.contactInfo[state.defaultInfo]?.phone_code_country;
      setCountry(pref && pref !== "Prefijo" ? pref : "+57");
    }
  }, [selectedCareer, institute, faculty, isEditingContactInfo]);

  const handleChangeContactInfo = (
    event: React.MouseEvent<HTMLElement>,
    contactInfoDefault: DefaultValue
  ) => {
    if (contactInfoDefault != null) {
      event.preventDefault();
      setDefaultContactInfoValue(contactInfoDefault);
      const newCountry =
        editableContactInfo.contactInfo?.[contactInfoDefault]?.phone_code_country;
      setCountry(newCountry && newCountry !== "Prefijo" ? newCountry : "+57");
      setEditableContactInfo((prevInfo) => ({
        ...prevInfo,
        contactInfo: {
          ...prevInfo.contactInfo,
          default: contactInfoDefault,
        },
      }));
    }
  };

  const handleChangeContactSocial = (
    event: React.MouseEvent<HTMLElement>,
    contactSocialDefault: DefaultValue
  ) => {
    if (contactSocialDefault != null) {
      event.preventDefault();
      setDefaultContactSocialValue(contactSocialDefault);
      setEditableContactInfo((prevInfo) => ({
        ...prevInfo,
        contactSocialMedia: {
          ...prevInfo.contactSocialMedia,
          default: contactSocialDefault,
        },
      }));
    }
  };

  const handleContactInfoChange = (
    event: React.ChangeEvent<any> | SelectChangeEvent<any>
  ) => {
    const { name, value } = event.target;
    switch (name) {
      case "contactEmail":
        setEmailError(value ? !isValidEmail(value) : false);
        break;
      case "phone_code_country":
        setCountry(value);
        break;
      case "contactPhone":
        setPhoneError(value ? !isValidPhone(value) : false);
        break;
      default:
        break;
    }
    setEditableContactInfo((prevInfo) => ({
      ...prevInfo,
      contactInfo: {
        ...prevInfo.contactInfo,
        default: prevInfo.contactInfo?.default || defaultContactInfoValue || "ies",
        career: {
          ...prevInfo.contactInfo?.career,
          [name]: value,
        },
      },
      ...(name === "webSite" ? { webSiteCareer: value } : {}),
      [name]: value,
    }));
  };

  const handleSocialMediaChange = (event: React.ChangeEvent<any>) => {
    const { value, name } = event.target;

    switch (name) {
      case "whatsapp":
        setWhatsappError(value ? !isValidPhone(value) : false);
        break;
      case "facebook":
        setFacebookError(value ? !isValidUrl(value, ["fb", "facebook.com"]) : false);
        break;
      case "linkedin":
        setLinkedinError(value ? !isValidUrl(value, ["linkedin.com", "lnkd.in"]) : false);
        break;
      default:
        break;
    }
    setEditableContactInfo((prevInfo) => {
      const currentCareer = (prevInfo.contactSocialMedia?.career as ContactSocialMedia) || {};
      const career = {
        ...currentCareer,
        [name]: value,
      };

      return {
        ...prevInfo,
        contactSocialMedia: {
          ...prevInfo.contactSocialMedia,
          default: prevInfo.contactSocialMedia?.default || defaultContactSocialValue || "ies",
          career,
        },
      };
    });
  };

  async function handleContactInfoSubmit() {
    try {
      if (selectedCareer.id) {
        const activeContact = editableContactInfo.contactInfo?.[defaultContactInfoValue];
        const data = {
          ...editableContactInfo,
          contactInfo: {
            ...editableContactInfo.contactInfo,
            default: defaultContactInfoValue,
          },
          contactSocialMedia: {
            ...editableContactInfo.contactSocialMedia,
            default: defaultContactSocialValue,
          },
          webSite: activeContact?.webSite || editableContactInfo.webSite || "",
          contactEmail: activeContact?.contactEmail || "",
          contactPhone: activeContact?.contactPhone || null,
          phone_code_country:
            activeContact?.phone_code_country && activeContact.phone_code_country !== "Prefijo"
              ? activeContact.phone_code_country
              : "+57",
        };
        console.warn("Updating contact information in Database ...");

        await careersService.update(
          selectedCareer.uidInstitution,
          selectedCareer.uidFaculty,
          selectedCareer.id,
          data
        );
        updateCareer(data, selectedCareer.id);
        console.warn("Updated in Database!");
        Swal.fire({
          title: "Los cambios se han guardado exitosamente",
          icon: "success",
          timer: 2000,
        });
      }
      setIsEditingContactInfo(false);
    } catch (error) {
      console.error("Error updating contact information", error);
    }
  }

  const handleContactInfoSwitchChange = (
    switchName: "hasFacebook" | "hasWhatsapp" | "hasLinkedin"
  ) => {
    setEditableContactInfo((prevValues) => {
      const currentCareer = (prevValues.contactSocialMedia?.career || {}) as ContactSocialMedia;
      const updatedCareer: ContactSocialMedia = {
        ...currentCareer,
        [switchName]: !currentCareer[switchName],
      };
      return {
        ...prevValues,
        contactSocialMedia: {
          ...prevValues.contactSocialMedia,
          default: prevValues.contactSocialMedia?.default || defaultContactSocialValue || "ies",
          career: updatedCareer,
        },
      };
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        flexWrap: "wrap",
        position: "relative",
      }}
    >
      {isEditingContactInfo ? (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            sx={{
              position: "absolute",
              right: { xs: 0, sm: 0 },
              top: { xs: "35px", sm: "40px" },
              marginBottom: { xs: "20px", sm: "0" },
              display: "flex",
            }}
            onClick={handleContactInfoSubmit}
          >
            Guardar
          </Button>
          {!isEditable && (
            <Typography variant="h5" sx={{ margin: "0", color: "red" }}>
              {premiumPlan}
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            sx={{
              position: "absolute",
              right: { xs: 0, sm: 0 },
              top: { xs: "35px", sm: "40px" },
              marginBottom: { xs: "20px", sm: "0" },
              display: "flex",
            }}
            onClick={() => setIsEditingContactInfo(!isEditingContactInfo)}
          >
            Editar
          </Button>
        </Box>
      )}
      <Typography
        sx={{
          padding: "20px 0",
          color: colors.grey[100],
          marginTop: "10px",
        }}
        variant="h3"
      >
        Información de Contacto
      </Typography>

      <hr style={{ width: "100%", marginBottom: "25px" }} />

      {isEditingContactInfo && (
        <Box sx={{ marginBottom: "20px", width: "100%" }}>
          <ToggleButtonGroup
            color="primary"
            value={defaultContactInfoValue}
            exclusive
            onChange={handleChangeContactInfo}
            aria-label="Platform"
          >
            <ToggleButton value="ies">
              Reusa datos de la Institución
            </ToggleButton>
            <ToggleButton value="faculty">
              Reusa datos de la Facultad
            </ToggleButton>
            <ToggleButton value="career">Usa datos personalizados</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Box
        sx={{
          marginBottom: "20px",
          display: "flex",
          width: "100%",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold" }}
          color={colors.grey[100]}
        >
          Web/Enlace/Brochure
        </Typography>
        <EditableInput
          key={`webSite_${defaultContactInfoValue}`}
          value={
            editableContactInfo.contactInfo?.[defaultContactInfoValue]?.webSite || ""
          }
          name="webSite"
          type="text"
          isEditing={
            isEditingContactInfo && defaultContactInfoValue === fieldsToEdit
          }
          onChange={handleContactInfoChange}
          isDisabled={!isEditable}
        />
      </Box>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          display: { xs: "flex", sm: "grid" },
          flexDirection: "column",
          gridTemplateColumns: {
            sm: "repeat(1,minmax(0,100%))",
            md: "repeat(2,minmax(0,100%))",
          },
          gap: "20px",
        }}
      >
        <Box
          sx={{
            marginBottom: "20px",
            display: "flex",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold" }}
            color={colors.grey[100]}
          >
            Email/Contacto admisiones
          </Typography>
          <EditableInput
            key={`contactEmail_${defaultContactInfoValue}`}
            value={
              editableContactInfo.contactInfo?.[defaultContactInfoValue]
                ?.contactEmail || ""
            }
            name="contactEmail"
            type="text"
            error={emailError}
            helperText={
              emailError
                ? "El correo electrónico debe tener la extensión .edu"
                : ""
            }
            isEditing={
              isEditingContactInfo && defaultContactInfoValue === fieldsToEdit
            }
            onChange={handleContactInfoChange}
            isDisabled={!isEditable}
          />
        </Box>

        <Box
          sx={{
            marginBottom: "20px",
            display: "flex",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold" }}
            color={colors.grey[100]}
          >
            Teléfono de contacto
          </Typography>
          {isEditingContactInfo && defaultContactInfoValue === fieldsToEdit ? (
            <FormControl
              id="phone"
              variant="outlined"
              fullWidth
              size="small"
              sx={{ display: "flex", flexDirection: "row" }}
            >
              <Select
                value={country && country !== "Prefijo" ? country : "+57"}
                id="phone_code_country"
                label="Prefijo"
                name="phone_code_country"
                onChange={handleContactInfoChange}
                disabled={!isEditable}
                sx={{
                  display: "flex",
                  border: "none",
                  width: { xs: "40%", sm: "45%" },
                  color: colors.grey[100],
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 200,
                      width: "40ch",
                      right: 0,
                      margin: "0 0 0 24px",
                      color: colors.grey[100],
                    },
                  },
                }}
              >
                {dialCodes.map((c) => (
                  <MenuItem key={c.code} value={c.dial_code}>
                    ({c.dial_code}) {c.name}
                  </MenuItem>
                ))}
              </Select>
              <TextField
                id="contactPhone"
                name="contactPhone"
                type="text"
                value={
                  editableContactInfo.contactInfo?.[defaultContactInfoValue]
                    ?.contactPhone ?? ""
                }
                fullWidth
                autoComplete="off"
                variant="outlined"
                onChange={handleContactInfoChange}
                disabled={!isEditable}
                error={phoneError}
                helperText={phoneError ? "Sólo se permiten números" : ""}
                InputLabelProps={{
                  style: { color: colors.grey[100], fontSize: "16px" },
                }}
                InputProps={{
                  style: {
                    color: colors.grey[100],
                    fontSize: "16px",
                    width: "100%",
                  },
                }}
              />
            </FormControl>
          ) : (
            <Typography variant="h5" color={colors.grey[200]}>
              {(() => {
                const phone =
                  editableContactInfo.contactInfo?.[defaultContactInfoValue]
                    ?.contactPhone;
                const rawPref =
                  editableContactInfo.contactInfo?.[defaultContactInfoValue]
                    ?.phone_code_country;
                const pref = rawPref && rawPref !== "Prefijo" ? rawPref : "+57";
                if (!phone) return "";
                return `(${pref}) ${phone}`;
              })()}
            </Typography>
          )}
        </Box>
      </Box>

      <Typography
        sx={{
          padding: "20px 0",
          color: colors.grey[100],
          marginTop: "10px",
        }}
        variant="h4"
      >
        Redes Sociales
      </Typography>

      {isEditingContactInfo && (
        <Box sx={{ marginBottom: "20px", width: "100%" }}>
          <ToggleButtonGroup
            color="primary"
            value={defaultContactSocialValue}
            exclusive
            onChange={handleChangeContactSocial}
            aria-label="Platform"
          >
            <ToggleButton value="ies">
              Reusa datos de la Institución
            </ToggleButton>
            <ToggleButton value="faculty">
              Reusa datos de la Facultad
            </ToggleButton>
            <ToggleButton value="career">Usa datos personalizados</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Box
        sx={{
          position: "relative",
          width: "100%",
          display: { xs: "flex", sm: "grid" },
          flexDirection: "column",
          gridTemplateColumns: {
            sm: "repeat(1,minmax(0,100%))",
            md: "repeat(2,minmax(0,100%))",
          },
          gap: "20px",
        }}
      >
        {/*-----Whatsapp-----*/}
        <Box
          sx={{
            marginBottom: "20px",
            display: "flex",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold" }}
            color={colors.grey[100]}
          >
            Whatsapp
          </Typography>
          <EditableInput
            key={`whatsapp_${defaultContactSocialValue}`}
            value={
              editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.whatsapp ?? ""
            }
            name="whatsapp"
            type="text"
            error={
              whatsappError &&
              (editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.hasWhatsapp ?? false)
            }
            helperText={
              whatsappError &&
              editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.hasWhatsapp
                ? "Sólo se permiten números y debe incluir el código del pais. Ej. 573115552244"
                : ""
            }
            isEditing={
              isEditingContactInfo && defaultContactSocialValue === fieldsToEdit
            }
            isDisabled={
              !isEditable ||
              !editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.hasWhatsapp
            }
            onChange={handleSocialMediaChange}
          />
        </Box>

        <Box
          sx={{
            marginBottom: "20px",
            display: "flex",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold" }}
            color={colors.grey[100]}
          >
            Whatsapp Activo
          </Typography>
          {isEditingContactInfo &&
          defaultContactSocialValue === fieldsToEdit ? (
            <SwitchButton
              checked={
                editableContactInfo.contactSocialMedia?.career?.hasWhatsapp ??
                false
              }
              setChecked={() => handleContactInfoSwitchChange("hasWhatsapp")}
              isDisabled={!isEditable}
            />
          ) : (
            <Typography variant="h5" color={colors.grey[500]}>
              {editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.hasWhatsapp
                ? "Si"
                : "No"}
            </Typography>
          )}
        </Box>

        {/*-----Linkedin-----*/}
        <Box
          sx={{
            marginBottom: "20px",
            display: "flex",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold" }}
            color={colors.grey[100]}
          >
            Linkedin
          </Typography>
          <EditableInput
            key={`linkedin_${defaultContactSocialValue}`}
            value={
              editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.linkedin ?? ""
            }
            name="linkedin"
            type="text"
            error={
              linkedinError &&
              (editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.hasLinkedin ?? false)
            }
            helperText={
              linkedinError &&
              editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.hasLinkedin
                ? "La URL debe tener dominio linkedin.com o lnkd.in"
                : ""
            }
            isEditing={
              isEditingContactInfo && defaultContactSocialValue === fieldsToEdit
            }
            isDisabled={
              !isEditable ||
              !editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.hasLinkedin
            }
            onChange={handleSocialMediaChange}
          />
        </Box>

        <Box
          sx={{
            marginBottom: "20px",
            display: "flex",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold" }}
            color={colors.grey[100]}
          >
            Linkedin Activo
          </Typography>
          {isEditingContactInfo &&
          defaultContactSocialValue === fieldsToEdit ? (
            <SwitchButton
              checked={
                editableContactInfo.contactSocialMedia?.career?.hasLinkedin ??
                false
              }
              setChecked={() => handleContactInfoSwitchChange("hasLinkedin")}
              isDisabled={!isEditable}
            />
          ) : (
            <Typography variant="h5" color={colors.grey[500]}>
              {editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.hasLinkedin
                ? "Si"
                : "No"}
            </Typography>
          )}
        </Box>

        {/*-----Facebook-----*/}
        <Box
          sx={{
            marginBottom: "20px",
            display: "flex",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold" }}
            color={colors.grey[100]}
          >
            Facebook
          </Typography>
          <EditableInput
            key={`facebook_${defaultContactSocialValue}`}
            value={
              editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.facebook ?? ""
            }
            name="facebook"
            type="text"
            error={
              facebookError &&
              (editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.hasFacebook ?? false)
            }
            helperText={
              facebookError &&
              editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.hasFacebook
                ? "La URL debe tener dominio facebook o fb"
                : ""
            }
            isEditing={
              isEditingContactInfo && defaultContactSocialValue === fieldsToEdit
            }
            isDisabled={
              !isEditable ||
              !editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.hasFacebook
            }
            onChange={handleSocialMediaChange}
          />
        </Box>

        <Box
          sx={{
            marginBottom: "20px",
            display: "flex",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold" }}
            color={colors.grey[100]}
          >
            Facebook Activo
          </Typography>
          {isEditingContactInfo &&
          defaultContactSocialValue === fieldsToEdit ? (
            <SwitchButton
              checked={
                editableContactInfo.contactSocialMedia?.career?.hasFacebook ??
                false
              }
              setChecked={() => handleContactInfoSwitchChange("hasFacebook")}
              isDisabled={!isEditable}
            />
          ) : (
            <Typography variant="h5" color={colors.grey[500]}>
              {editableContactInfo.contactSocialMedia?.[defaultContactSocialValue]
                ?.hasFacebook
                ? "Si"
                : "No"}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
